import { resolveActiveContextGroup } from '@/core/user';
import { useAppSelector } from '@/store';

import { canViewEngagementSurfaces, isEngagementsActiveForContext } from './gate';

/**
 * Single gate every consulting-engagements route/mount must check (wired from
 * FE2 on). Returns false — nothing renders — until the api says both that this
 * environment has engagements and that the viewer's account does.
 *
 * Both halves of the answer now come from the api (ENG-5715):
 * `engagementsAvailable` for the environment and `Group.engagementsEnabled` for
 * the account, the latter resolved from an `engagement_billing_settings` row so
 * the staged rollout (QA → design partner → GA, ENG-5446) is a mutation rather
 * than a deploy. ENG-5607 had moved this off GrowthBook to a deploy-controlled
 * `VITE_*` allowlist for good reasons that still hold — a dashboard control
 * plane is how a feature ends up half-on — but ENG-5669 then replaced the api's
 * own copy of that allowlist, leaving the two repos able to disagree about the
 * same account. One answer, from the side that owns it.
 *
 * The resolution rules live in `gate.ts` as a pure predicate — see there. This
 * hook is only the Redux binding, so callers that already hold `viewer` and
 * `userContext` can skip the store entirely.
 *
 * It binds the VISIBILITY policy, `canViewEngagementSurfaces`, which includes
 * the admin and expert overrides. If you need the account question instead —
 * "does the account being operated in have this product" — call
 * `isEngagementsActiveForContext` from `gate.ts` directly. The hook keeps its
 * older name because the README documents it as the route/mount convention and
 * FE6 is wiring against it; renaming it is tracked separately.
 */
export function useConsultingEngagementsEnabled(): boolean {
  const viewer = useAppSelector((state) => state.viewer);
  const userContext = useAppSelector((state) => state.ui.userContext);
  return canViewEngagementSurfaces(viewer, userContext);
}

/**
 * The ACCOUNT question, bound to the store: does the account the viewer is
 * operating in have this product?
 *
 * Use this, not `useConsultingEngagementsEnabled`, for anything that lets a
 * customer ACT — creating an engagement above all. The visibility hook carries
 * an `admin` override that returns true regardless of account, which is right
 * for "should this nav item render" and wrong for "may this person start
 * something": it would offer creation to OF staff on every profile on the
 * platform, including accounts never selected for the staged rollout
 * (ENG-5446), against the standing rule that staff read everything and act on
 * nothing (ENG-5683).
 *
 * It drops the expert override too, deliberately. Hiring is a customer-side
 * act; an expert has no account axis to place them on and no business starting
 * one.
 *
 * Where the surface knows which account it belongs to — a consultation, a
 * project — prefer `isEngagementsEnabledForGroup` on that account instead. This
 * hook answers for the viewer's own context, which is the best a surface with
 * no account of its own can do.
 */
export function useEngagementsActiveForAccount(): boolean {
  const viewer = useAppSelector((state) => state.viewer);
  const userContext = useAppSelector((state) => state.ui.userContext);
  return isEngagementsActiveForContext(viewer, userContext);
}

/** The account the viewer is acting in, as much of it as a scoped read needs. */
export interface ActiveAccount {
  id: string;
  /** For telling the viewer WHICH account a scoped list was scoped to. */
  name?: string | null;
}

/**
 * WHICH account is the viewer acting in?
 *
 * The companion to `useEngagementsActiveForAccount`, which answers whether that
 * account has the product. Anything that has to SCOPE data to the account —
 * rather than merely decide whether to render — needs the id as well as the
 * yes/no, and deriving it a second time at the call site is how the two answers
 * drift apart.
 *
 * Same resolution as the gate, so the two cannot disagree: the "view as"
 * context when there is one, else the viewer's default account group, resolved
 * against `viewer.groups`. That list is built from ACTIVE `team_members` rows
 * api-side, which is what makes the id safe to hand to a group-scoped query —
 * `projects(group_id:)` reads `group:view_members` for non-staff callers, a
 * grant that active membership carries.
 *
 * Undefined means there is no account context at all: an expert, a signed-out
 * viewer, or the ENG-5680 gap where staff view-as a group they are not a member
 * of. Callers must skip rather than substitute a blank — `group_id: ""` is not
 * "this account", it is "every account", which is the ENG-5729 leak.
 */
export function useActiveAccount(): ActiveAccount | undefined {
  const viewer = useAppSelector((state) => state.viewer);
  const userContext = useAppSelector((state) => state.ui.userContext);
  const group = resolveActiveContextGroup(viewer, userContext);
  if (!group?.id) return undefined;
  return { id: group.id, name: group.name };
}
